import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Trash2, BookHeart } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function PostsPage({ endpoint, eyebrow, title, lead, placeholder, icon: HeaderIcon = MessageCircle, emptyText }) {
  const { isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [author, setAuthor] = useState(() => localStorage.getItem("ebel_post_author") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/${endpoint}`).then((r) => setPosts(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/${endpoint}`, { author: author.trim() || "Anonym", text: text.trim() });
      localStorage.setItem("ebel_post_author", author.trim());
      setText("");
      await load();
      toast.success("Beitrag gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Beitrag wirklich löschen?")) return;
    await api.delete(`/${endpoint}/${id}`);
    await load();
    toast.success("Beitrag entfernt");
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid={`${endpoint}-page`}>
      <div className="text-center mb-12">
        <p className="label-eyebrow mb-3">{eyebrow}</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">{title}</h1>
        <p className="text-[#4A4F46] mt-4 max-w-xl mx-auto leading-relaxed">{lead}</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E5DFD3] rounded-2xl p-6 mb-10 shadow-[0_8px_30px_-20px_rgba(72,99,74,0.3)]" data-testid={`${endpoint}-form`}>
        <label className="label-eyebrow block mb-2">Mein Name</label>
        <input
          type="text"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="z. B. Anna Ebel"
          maxLength={80}
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#48634A]/30 mb-4"
          data-testid={`${endpoint}-author`}
        />
        <label className="label-eyebrow block mb-2">Beitrag</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder={placeholder}
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={`${endpoint}-text`}
          required
        />
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-[#4A4F46]">{text.length}/2000</span>
          <button
            type="submit"
            disabled={busy || !text.trim()}
            className="btn-primary rounded-xl px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid={`${endpoint}-submit`}
          >
            <Send className="w-4 h-4" /> Senden
          </button>
        </div>
      </form>

      {posts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5DFD3] rounded-2xl">
          <HeaderIcon className="w-10 h-10 text-[#48634A] mx-auto mb-3" />
          <p className="font-serif text-2xl">{emptyText}</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {posts.map((p) => (
            <li key={p.id} className="bg-white border border-[#E5DFD3] rounded-2xl p-5 fade-in" data-testid={`${endpoint}-item-${p.id}`}>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div>
                  <span className="font-serif text-xl text-[#1A1C19]">{p.author}</span>
                  <span className="ml-3 text-xs text-[#4A4F46]">{formatDate(p.created_at)}</span>
                </div>
                {isAdmin && (
                  <button onClick={() => remove(p.id)} className="text-[#B74134] hover:text-[#8a3025] text-xs flex items-center gap-1" data-testid={`${endpoint}-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Entfernen
                  </button>
                )}
              </div>
              <p className="text-[#1A1C19] leading-relaxed whitespace-pre-wrap">{p.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function Communication() {
  return (
    <PostsPage
      endpoint="messages"
      eyebrow="Familien-Pinnwand"
      title="Kommunikation"
      lead="Schreib eine kurze Nachricht an die Familie – Fragen, Vorschläge, Ankündigungen."
      placeholder="Was möchtest du der Familie mitteilen?"
      icon={MessageCircle}
      emptyText="Noch keine Nachrichten – sei die/der Erste!"
    />
  );
}

export function Guestbook() {
  return (
    <PostsPage
      endpoint="guestbook"
      eyebrow="Erinnerungen & Grüße"
      title="Gästebuch"
      lead="Trage hier deine Gedanken, schönsten Erinnerungen oder Grüße an die Familie ein."
      placeholder="Was möchtest du festhalten?"
      icon={BookHeart}
      emptyText="Das Gästebuch wartet auf den ersten Eintrag."
    />
  );
}
